import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import {
  getExamCenters,
  postExamCenter,
  patchExamCenter,
  deleteExamCenter,
} from "../../../services/olympiad/examCenterService";
import { getRegions } from "../../../services/olympiad/regionService";
import ExamCenterPageHeader from "./PageHeader";
import ExamCenterFilters from "./Filters";
import ExamCenterForm from "./ExamCenterForm";
import { useExamCenterColumns } from "./columns";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";

export default function ExamCenters() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  // Filters
  const [regionFilter, setRegionFilter] = useState(null);
  const [isFullFilter, setIsFullFilter] = useState(null);

  // Options
  const [regions, setRegions] = useState([]);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingExamCenter, setEditingExamCenter] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    region: null,
  });

  useEffect(() => {
    getRegions({})
      .then((res) => {
        setRegions(
          (res.results || []).map((r) => ({ label: r.name, value: r.uuid }))
        );
      })
      .catch((err) => console.error("Error fetching regions:", err));
  }, []);

  const fetchData = () => {
    setLoading(true);
    getExamCenters({
      region: regionFilter,
      is_full: isFullFilter,
      page: page,
      page_size: 50,
    })
      .then((res) => {
        setData(res.results || []);
        setCount(res.count || 0);
        setPageCount(Math.ceil((res.count || 0) / 50));
      })
      .catch((err) => {
        console.error("Error fetching exam centers:", err);
        toast.error(t("admin.error_fetching_data"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [page, regionFilter, isFullFilter]);

  const handleCreate = () => {
    postExamCenter({
      name: formData.name,
      region_uuid: formData.region,
    })
      .then(() => {
        toast.success(t("admin.exam_center_created"));
        setIsCreateModalOpen(false);
        setFormData({ name: "", region: null });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_creating_exam_center")
        );
      });
  };

  const handleEdit = (examCenter) => {
    setEditingExamCenter(examCenter);
    setFormData({
      name: examCenter.name || "",
      region: examCenter.region?.uuid || null,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    patchExamCenter(editingExamCenter.uuid, {
      name: formData.name,
      region_uuid: formData.region,
    })
      .then(() => {
        toast.success(t("admin.exam_center_updated"));
        setIsEditModalOpen(false);
        setEditingExamCenter(null);
        setFormData({ name: "", region: null });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_updating_exam_center")
        );
      });
  };

  const handleDelete = (examCenter) => {
    if (toast.isActive(examCenter.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>
          {t("admin.confirm_delete_exam_center", {
            examCenter: examCenter.name,
          })}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(examCenter.uuid);
              deleteExamCenter(examCenter.uuid)
                .then(() => {
                  toast.success(t("admin.exam_center_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail ||
                      t("admin.error_deleting_exam_center")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(examCenter.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: examCenter.uuid }
    );
  };

  const columns = useExamCenterColumns(handleEdit, handleDelete);

  return (
    <div className="p-6">
      <ExamCenterPageHeader onCreateClick={() => setIsCreateModalOpen(true)} />

      <ExamCenterFilters
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
        isFullFilter={isFullFilter}
        setIsFullFilter={setIsFullFilter}
        regions={regions}
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
      />

      <ExamCenterForm
        isOpen={isCreateModalOpen}
        isEdit={false}
        formData={formData}
        setFormData={setFormData}
        regions={regions}
        onClose={() => {
          setIsCreateModalOpen(false);
          setFormData({ name: "", region: null });
        }}
        onSubmit={handleCreate}
      />

      <ExamCenterForm
        isOpen={isEditModalOpen}
        isEdit={true}
        formData={formData}
        setFormData={setFormData}
        regions={regions}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingExamCenter(null);
          setFormData({ name: "", region: null });
        }}
        onSubmit={handleUpdate}
      />
    </div>
  );
}
